"use client"

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import * as React from "react"

import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root ref={ref} className={cn("grid gap-2", className)} {...props} />
))
RadioGroup.displayName = "RadioGroup"

const RadioGroupIndicator = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Indicator>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Indicator>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Indicator
    ref={ref}
    className={cn("flex items-center justify-center", className)}
    {...props}
  >
    <div className="bg-ui-bg-base shadow-details-contrast-on-bg-interactive h-1.5 w-1.5 rounded-full" />
  </RadioGroupPrimitive.Indicator>
))
RadioGroupIndicator.displayName = "RadioGroup.Indicator"

const Item = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      "group relative flex h-5 w-5 items-center justify-center outline-none",
      className
    )}
    {...props}
  >
    <div
      className={cn(
        "shadow-borders-base bg-ui-bg-base transition-fg flex h-[14px] w-[14px] items-center justify-center rounded-full",
        "group-hover:group-enabled:group-data-[state=unchecked]:bg-ui-bg-base-hover",
        "group-data-[state=checked]:bg-ui-bg-interactive group-data-[state=checked]:shadow-borders-interactive-with-shadow",
        "group-focus-visible:!shadow-borders-interactive-with-focus",
        "group-disabled:cursor-not-allowed group-disabled:opacity-50"
      )}
    >
      <RadioGroupIndicator />
    </div>
  </RadioGroupPrimitive.Item>
))
Item.displayName = "RadioGroup.Item"

const ChoiceBox = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    id?: string
    label: string
    description: React.ReactNode
    value: string
  }
>(({ className, id, label, description, value, ...props }, ref) => {
  const generatedId = React.useId()
  if (!id) {
    id = generatedId
  }
  const descriptionId = `${id}-description`
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "shadow-borders-base bg-ui-bg-base focus-visible:shadow-borders-interactive-with-focus transition-fg group flex items-start gap-x-2 rounded-lg p-3 outline-none",
        "hover:enabled:bg-ui-bg-base-hover",
        "data-[state=checked]:shadow-borders-interactive-with-shadow",
        "group-disabled:cursor-not-allowed group-disabled:opacity-50",
        className
      )}
      value={value}
      id={id}
      aria-describedby={descriptionId}
      {...props}
    >
      <div className="flex h-5 w-5 items-center justify-center">
        <div
          className={cn(
            "shadow-borders-base bg-ui-bg-base group-data-[state=checked]:bg-ui-bg-interactive group-data-[state=checked]:shadow-borders-interactive-with-shadow transition-fg flex h-3.5 w-3.5 items-center justify-center rounded-full",
            "group-hover:group-enabled:group-data-[state=unchecked]:bg-ui-bg-base-hover"
          )}
        >
          <RadioGroupIndicator />
        </div>
      </div>
      <div className="flex flex-col items-start">
        <Label
          htmlFor={id}
          size="small"
          weight="plus"
          className="group-disabled:text-ui-fg-disabled cursor-pointer group-disabled:cursor-not-allowed"
        >
          {label}
        </Label>
        <p
          id={descriptionId}
          className="txt-small text-ui-fg-subtle group-disabled:text-ui-fg-disabled text-left"
        >
          {description}
        </p>
      </div>
    </RadioGroupPrimitive.Item>
  )
})
ChoiceBox.displayName = "RadioGroup.ChoiceBox"

const RadioGroupWithParts = Object.assign(RadioGroup, { Item, ChoiceBox })

export { RadioGroupWithParts as RadioGroup, Item as RadioGroupItem, RadioGroupIndicator }
